/**
 * Day 8, evidence-backed creative directions, from that stage onward: the
 * bounded generation, each direction's objective, mechanism, angle, proof
 * and limits, the review controls, and the way back to the Strategy Brief.
 */

import type { ReactNode } from 'react';

import { ANALYSIS_STAGES } from '@mouja/domain/analysis';

import {
  generateAnalysisCreativeDirections,
  newIdempotencyKey,
  reviewAnalysisCreativeDirections,
  type AnalysisDay8State,
  type AnalysisRunSummary,
} from '../../api.js';
import { Button, InlineAlert, Tag, TextAreaField } from '../../ui.js';
import { SectionHeading } from '../common.js';
import { parseObjectJson } from './review-json.js';
import { card } from './run-page-styles.js';
import { displayScalar, toneFor } from './run-state-labels.js';
import type { RunMutation } from './useAnalysisRunMutation.js';
import type { ReviewDrafts } from './useReviewDrafts.js';

export function CreativeDirectionsSection({
  id,
  run,
  day8,
  busy,
  mutate,
  drafts,
}: {
  readonly id: string;
  readonly run: AnalysisRunSummary;
  readonly day8: AnalysisDay8State;
  readonly busy: string | null;
  readonly mutate: RunMutation;
  readonly drafts: ReviewDrafts;
}): ReactNode {
  const { directionReviewJson, setDirectionReviewJson } = drafts;
  if (
    ANALYSIS_STAGES.findIndex((stage) => stage === run.currentStage) <
    ANALYSIS_STAGES.indexOf('CREATIVE_DIRECTIONS')
  ) {
    return null;
  }

  return (
    <section id="directions-review" style={{ marginBottom: 'var(--space-lg)' }}>
      <SectionHeading>Evidence-backed creative directions</SectionHeading>
      <div style={{ ...card, display: 'grid', gap: 12 }}>
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
          Directions consume approved opportunities only. They remain bounded
          objectives, mechanisms, angles, proof structures and CTA directions—not
          scripts, storyboards, shot lists, audio, or video.
        </p>
        {run.currentStage === 'CREATIVE_DIRECTIONS' &&
        run.executionState === 'PENDING' ? (
          <Button
            variant="primary"
            busy={busy === 'creative-directions'}
            onClick={() =>
              void mutate('creative-directions', () =>
                generateAnalysisCreativeDirections(id, newIdempotencyKey()),
              )
            }
          >
            Generate bounded creative directions
          </Button>
        ) : null}
      </div>

      {day8.creativeDirectionCandidates.length === 0 &&
      day8.creativeDirectionManifest ? (
        <InlineAlert
          tone="warning"
          title="Zero creative directions were generated because no approved opportunity supports one. The run can still complete honestly."
        />
      ) : null}

      {day8.creativeDirectionCandidates.length > 0 ? (
        <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
          {day8.creativeDirectionCandidates.map((artifact) => {
            const direction = artifact.payload;
            return (
              <article key={artifact.id} style={card}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Tag tone="info">ORDER {displayScalar(direction['order'], '?')}</Tag>
                  <Tag
                    tone={toneFor(displayScalar(direction['feasibility'], 'UNKNOWN'))}
                  >
                    {displayScalar(direction['feasibility'], 'UNKNOWN')}
                  </Tag>
                </div>
                <h3 style={{ font: 'var(--type-heading-sm)' }}>
                  {displayScalar(direction['title'], 'Creative direction')}
                </h3>
                <dl className="mj-form-two-column">
                  <div>
                    <dt>Objective</dt>
                    <dd>{displayScalar(direction['objective'], '—')}</dd>
                  </div>
                  <div>
                    <dt>Mechanism</dt>
                    <dd>{displayScalar(direction['coreMechanism'], '—')}</dd>
                  </div>
                  <div>
                    <dt>Angle</dt>
                    <dd>{displayScalar(direction['angle'], '—')}</dd>
                  </div>
                  <div>
                    <dt>CTA</dt>
                    <dd>{displayScalar(direction['ctaDirection'], 'None')}</dd>
                  </div>
                  <div>
                    <dt>Optional example hook</dt>
                    <dd>{displayScalar(direction['optionalExampleHook'], 'None')}</dd>
                  </div>
                </dl>
                <h4>Proof structure</h4>
                <p>{displayScalar(direction['proofStructure'], '—')}</p>
                <h4>Evidence and limitations</h4>
                <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {JSON.stringify(
                    {
                      findings: direction['supportingFindingArtifactIds'] ?? [],
                      evidence: direction['evidenceReferences'] ?? [],
                      limitations: direction['limitations'] ?? [],
                    },
                    null,
                    2,
                  )}
                </pre>
                <h4>Adaptation / copying boundary</h4>
                <p>{displayScalar(direction['copyingAdaptationNote'], '—')}</p>
              </article>
            );
          })}
        </div>
      ) : null}

      {run.currentStage === 'CREATIVE_DIRECTIONS' &&
      run.executionState === 'AWAITING_REVIEW' ? (
        <div style={{ ...card, display: 'grid', gap: 12, marginTop: 12 }}>
          <InlineAlert
            tone="warning"
            title="Nothing is auto-approved. Corrections may polish bounded direction fields but cannot alter evidence, Offer, opportunity, or version locks."
          />
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
            Set APPROVED, REJECTED, CORRECTED, or INSUFFICIENT_EVIDENCE. Use a unique
            order from 1–3 for approved/corrected directions. A correction object may
            contain title, objective, angle, proofStructure, ctaDirection, or
            optionalExampleHook.
          </p>
          <TextAreaField
            label="Creative-direction decisions JSON"
            value={directionReviewJson}
            onChange={setDirectionReviewJson}
            rows={16}
            maxLength={1_800_000}
          />
          <Button
            variant="primary"
            busy={busy === 'direction-review'}
            onClick={() =>
              void mutate('direction-review', () =>
                reviewAnalysisCreativeDirections(
                  id,
                  newIdempotencyKey(),
                  directionReviewJson.trim()
                    ? parseObjectJson(directionReviewJson, 'Creative-direction review')
                    : { decisions: [] },
                ),
              )
            }
          >
            Freeze directions and complete the Strategy Brief
          </Button>
        </div>
      ) : null}

      {day8.strategyBrief ? (
        <p style={{ marginTop: 12 }}>
          <a href="#strategy-brief">Back to the presentation-ready Strategy Brief</a>
        </p>
      ) : null}
    </section>
  );
}
